import React, { useCallback, useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Truck, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api, { extractApiError } from "@/lib/api";
import { useOnboardVehicleOptions } from "@/hooks/useOnboardVehicleOptions";
import CreatableDropdownInput from "@/components/shared/CreatableDropdownInput";

export default function OnboardVehicleModal({ isOpen, onClose, onSuccess }) {
  const [organizations, setOrganizations] = useState([]);
  const [loadingOrgs, setLoadingOrgs] = useState(false);
  const [formData, setFormData] = useState({
    vehicle_category: "EMERGENCY_VEHICLE",
    vin: "",
    registration_number: "",
    make: "",
    model: "",
    year: "",
    vehicle_type: "",
    organization_id: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  const currentYear = new Date().getFullYear();
  const YEARS = Array.from({ length: currentYear - 1989 }, (_, i) => String(currentYear - i));
  const {
    makes,
    models,
    vehicleTypes,
    loading: optionsLoading,
  } = useOnboardVehicleOptions({ enabled: isOpen });

  const makeOptions = Array.isArray(makes) ? makes : [];
  const modelOptions = Array.isArray(models) ? models : [];
  const vehicleTypeOptions = Array.isArray(vehicleTypes) ? vehicleTypes : [];

  const fetchOrganizations = useCallback(async () => {
    setLoadingOrgs(true);
    try {
      const { data } = await api.get("/organizations", {
        params: { is_active: true, page_size: 100 },
      });
      setOrganizations(Array.isArray(data?.data) ? data.data : []);
    } catch (err) {
      console.error("Failed to load organizations", err);
      setOrganizations([]);
    } finally {
      setLoadingOrgs(false);
    }
  }, []);

  useEffect(() => {
    if (isOpen) {
      fetchOrganizations();
    }
  }, [isOpen, fetchOrganizations]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError("");

    if (!formData.vehicle_category) {
      setSubmitError("Vehicle category is required.");
      return;
    }

    if (!formData.registration_number.trim()) {
      setSubmitError("Registration number is required.");
      return;
    }

    if (!formData.vin.trim()) {
      setSubmitError("VIN is required.");
      return;
    }

    if (!formData.organization_id.trim()) {
      setSubmitError("Organization is required.");
      return;
    }

    const selectedOrg = organizations.find((org) => org.id === formData.organization_id.trim());
    if (!selectedOrg) {
      setSubmitError("Please select a valid organization.");
      return;
    }

    if (!["POLICE", "AMBULANCE", "FIRE_DEPARTMENT"].includes(selectedOrg.org_type)) {
      setSubmitError("Selected organization must be a police, ambulance, or fire department.");
      return;
    }

    if (formData.year && Number.isNaN(Number(formData.year))) {
      setSubmitError("Year must be a valid number.");
      return;
    }

    if (formData.year) {
      const yearValue = Number(formData.year);
      if (yearValue < 1900 || yearValue > 2100) {
        setSubmitError("Year must be between 1900 and 2100.");
        return;
      }
    }

    const payload = {
      vehicle_category: "EMERGENCY_VEHICLE",
      vin: formData.vin.trim(),
      ...(formData.registration_number.trim() ? { registration_number: formData.registration_number.trim() } : {}),
      ...(formData.make.trim() ? { make: formData.make.trim() } : {}),
      ...(formData.model.trim() ? { model: formData.model.trim() } : {}),
      ...(formData.year ? { year: Number(formData.year) } : {}),
      ...(formData.vehicle_type.trim() ? { vehicle_type: formData.vehicle_type.trim() } : {}),
      ...(formData.organization_id.trim() ? { organization_id: formData.organization_id.trim() } : {}),
    };

    setIsSubmitting(true);

    try {
      const { data } = await api.post("/vehicles", payload);
      const createdVehicle = data?.data || data;

      toast.success("Vehicle Onboarded", {
        description: `${formData.registration_number} has been successfully registered to the platform.`,
      });

      onSuccess?.(createdVehicle);
      setIsSubmitting(false);
      setSubmitError("");
      setFormData({
        vehicle_category: "EMERGENCY_VEHICLE",
        vin: "",
        registration_number: "",
        make: "",
        model: "",
        year: "",
        vehicle_type: "",
        organization_id: "",
      });
    } catch (err) {
      const { message } = extractApiError(err);
      setSubmitError(message);
      toast.error(message);
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="sm:max-w-[560px] rounded-[24px] border-0 shadow-2xl p-0 overflow-hidden bg-slate-50 max-h-[90vh] overflow-y-auto">
        <DialogHeader className="bg-white px-8 py-6 border-b border-slate-100">
          <div className="flex items-center gap-3 mb-1">
            <div className="w-10 h-10 rounded-xl bg-blue-50 flex items-center justify-center text-blue-600">
              <Truck size={20} />
            </div>
            <div>
              <DialogTitle className="text-xl font-black text-slate-900">Onboard New Vehicle</DialogTitle>
              <DialogDescription className="text-slate-500 text-sm">Register a vehicle in the platform fleet.</DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="p-8 space-y-5">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label className="text-[12px] font-bold text-slate-500 uppercase">Vehicle Category</Label>
              <Input
                value="EMERGENCY_VEHICLE"
                readOnly
                className="bg-white border-slate-200 h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="registration_number" className="text-[12px] font-bold text-slate-500 uppercase flex items-center gap-1.5">
                <ShieldCheck size={14} className="text-slate-400" /> Registration Number
              </Label>
              <Input 
                id="registration_number"
                placeholder="MH-01-AB-1234" 
                className="bg-white border-slate-200 h-11 font-mono font-bold"
                value={formData.registration_number}
                onChange={(e) => setFormData({ ...formData, registration_number: e.target.value })}
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="vin" className="text-[12px] font-bold text-slate-500 uppercase">VIN</Label>
            <Input
              id="vin"
              placeholder="e.g. MA1AB2CD3EF456789"
              className="bg-white border-slate-200 h-11 font-mono uppercase"
              value={formData.vin}
              onChange={(e) => setFormData({ ...formData, vin: e.target.value.toUpperCase() })}
              required
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="make" className="text-[12px] font-bold text-slate-500 uppercase">Make</Label>
              <CreatableDropdownInput
                id="make"
                placeholder="Select or type make"
                value={formData.make}
                onChange={(value) => setFormData({ ...formData, make: value })}
                options={makeOptions}
                loading={optionsLoading}
                noOptionsText="No makes available. Type a custom make."
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="model" className="text-[12px] font-bold text-slate-500 uppercase">Vehicle Model</Label>
              <CreatableDropdownInput
                id="model"
                placeholder="Select or type vehicle model"
                value={formData.model}
                onChange={(value) => setFormData({ ...formData, model: value })}
                options={modelOptions}
                loading={optionsLoading}
                noOptionsText="No models available. Type a custom model."
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="year" className="text-[12px] font-bold text-slate-500 uppercase">Year</Label>
              {/* <Input
                id="year"
                type="number"
                min="1900"
                max="2100"
                placeholder="e.g. 2024"
                className="bg-white border-slate-200 h-11"
                value={formData.year}
                onChange={(e) => setFormData({ ...formData, year: e.target.value })}
              /> */}
              <Select
    value={formData.year}
    onValueChange={(value) => setFormData({ ...formData, year: value })}
  >
    <SelectTrigger className="bg-white border-slate-200 h-11">
      <SelectValue placeholder="Select Year" />
    </SelectTrigger>

    <SelectContent>
      {YEARS.map((y) => (
        <SelectItem key={y} value={y}>
          {y}
        </SelectItem>
      ))}
    </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="vehicle_type" className="text-[12px] font-bold text-slate-500 uppercase">Vehicle Type</Label>
              <CreatableDropdownInput
                id="vehicle_type"
                placeholder="Select or type vehicle type"
                value={formData.vehicle_type}
                onChange={(value) => setFormData({ ...formData, vehicle_type: value })}
                options={vehicleTypeOptions}
                loading={optionsLoading}
                noOptionsText="No vehicle types available. Type a custom vehicle type."
              />
            </div>
          </div>

          {/* <div className="space-y-2">
            <Label className="text-[12px] font-bold text-slate-500 uppercase">Organization</Label>
            <Select
              value={formData.organization_id}
              onValueChange={(val) => setFormData({ ...formData, organization_id: val })}
              disabled={loadingOrgs}
            >
              <SelectTrigger className="bg-white border-slate-200 h-11">
                <SelectValue placeholder={loadingOrgs ? "Loading organizations..." : "Select emergency organization"} />
              </SelectTrigger>
              <SelectContent>
                {organizations
                  .filter((org) => ["POLICE", "AMBULANCE", "FIRE_DEPARTMENT"].includes(org.org_type))
                  .map((org) => (
                    <SelectItem key={org.id} value={org.id}>
                      {org.name}
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div> */}

          {submitError && (
            <div className="rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
              {submitError}
            </div>
          )}

          <div className="text-[11px] text-slate-500 bg-slate-100 rounded-xl px-3 py-2">
            Note: `USER_VEHICLE` is typically onboarded via QR flow. Use this form mainly for platform/admin onboarding.
          </div>

          <DialogFooter className="pt-4 gap-3">
            <Button type="button" variant="ghost" onClick={onClose} disabled={isSubmitting} className="font-bold text-slate-500">
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting} className="bg-slate-900 text-white hover:bg-slate-800 h-11 px-8 rounded-xl font-bold">
              {isSubmitting ? "Processing..." : "Complete Onboarding"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
